/* =============================================================
   SERVICES SECTION — Clear service cards with icons
   Addresses: Original site had a plain bullet list with no visual hierarchy
   Design: Warm white background, navy card headers, gold accents
   ============================================================= */

import { 
  FileText, CreditCard, Users, BarChart3, 
  RefreshCw, BookOpen, Calculator, Layers
} from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const services = [
  {
    icon: FileText,
    title: "BAS Preparation & Lodgement",
    description: "Accurate Business Activity Statements prepared and lodged on time, every time. Never miss an ATO deadline again.",
    highlight: true,
    href: "/bas-agent-perth",
  },
  {
    icon: CreditCard,
    title: "Bank Reconciliation",
    description: "Weekly reconciliation of all your accounts ensures your books always reflect reality. Catch errors before they become problems.",
    highlight: false,
    href: "/bookkeeping-services-perth",
  },
  {
    icon: Users,
    title: "Payroll Processing",
    description: "Full payroll management including PAYGW, superannuation payments, and Single Touch Payroll (STP) compliance.",
    highlight: false,
    href: "/payroll-bookkeeper-perth",
  },
  {
    icon: BarChart3,
    title: "Management Reporting",
    description: "Monthly profit & loss, balance sheet, and custom reports delivered straight to your inbox. Know your numbers.",
    highlight: false,
    href: null,
  },
  {
    icon: RefreshCw,
    title: "Accounts Payable & Receivable",
    description: "We manage your invoices, bills, and payment runs. Improve cash flow and maintain strong supplier relationships.",
    highlight: false,
    href: null,
  },
  {
    icon: BookOpen,
    title: "Xero Setup & Training",
    description: "Get up and running on Xero with expert setup and personalised training. Ongoing support whenever you need it.",
    highlight: false,
    href: "/xero-bookkeeper-perth",
  },
  {
    icon: Calculator,
    title: "Catchup & Cleanup Bookkeeping",
    description: "Fallen behind or inherited a mess? We'll get your books back on track quickly and accurately.",
    highlight: false,
    href: "/catch-up-bookkeeping-perth",
  },
  {
    icon: Layers,
    title: "Expense Tracking & Budgeting",
    description: "Annual budget creation, Xero integration, and monthly budget vs actuals tracking to keep you on course.",
    highlight: false,
    href: null,
  },
];

export default function ServicesSection() {
  const { ref, style } = useScrollAnimation({ type: 'fade', speed: 0.3 });
  return (
    <section ref={ref} style={style} id="services" className="py-20 lg:py-28 bg-[oklch(0.99_0.005_80)]">
      <div className="container">
        {/* Section header */}
        <div className="max-w-2xl mb-14">
          <div className="section-label mb-4">What We Do</div>
          <h2 className="text-3xl lg:text-4xl xl:text-5xl text-[oklch(0.22_0.06_250)] mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Everything Your Business Needs,{" "}
            <span className="italic text-[oklch(0.72_0.12_75)]">Nothing It Doesn't</span>
          </h2>
          <p className="text-[oklch(0.45_0.02_250)] text-lg leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
            From day-to-day transaction recording to complex payroll and BAS lodgement, 
            we handle every aspect of your bookkeeping with precision and care.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((service) => {
            const cardContent = (
              <>
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-4 ${
                  service.highlight
                    ? "bg-[oklch(0.72_0.12_75)/0.2]"
                    : "bg-[oklch(0.22_0.06_250)/0.07] group-hover:bg-[oklch(0.72_0.12_75)/0.12] transition-colors"
                }`}>
                  <service.icon
                    size={20}
                    className={service.highlight ? "text-[oklch(0.72_0.12_75)]" : "text-[oklch(0.22_0.06_250)]"}
                  />
                </div>
                <h3
                  className={`text-base font-semibold mb-2 leading-snug ${
                    service.highlight ? "text-white" : "text-[oklch(0.22_0.06_250)]"
                  }`}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {service.title}
                </h3>
                <p
                  className={`text-sm leading-relaxed ${
                    service.highlight ? "text-white/70" : "text-[oklch(0.45_0.02_250)]"
                  }`}
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                >
                  {service.description}
                </p>
                {service.href && (
                  <span className={`inline-block mt-3 text-xs font-medium ${
                    service.highlight ? "text-[oklch(0.72_0.12_75)]" : "text-[oklch(0.72_0.12_75)]"
                  }`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    Learn more →
                  </span>
                )}
              </>
            );
            const cardClass = `group rounded-xl p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
              service.highlight
                ? "bg-[oklch(0.22_0.06_250)] text-white"
                : "bg-white border border-[oklch(0.90_0.008_80)] hover:border-[oklch(0.72_0.12_75)/0.4]"
            } ${
              service.href ? "cursor-pointer" : "cursor-default"
            }`;
            return service.href ? (
              <a key={service.title} href={service.href} className={cardClass}>
                {cardContent}
              </a>
            ) : (
              <div key={service.title} className={cardClass}>
                {cardContent}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-[oklch(0.45_0.02_250)] mb-5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Not sure which services you need? Let's talk — the first consultation is always free.
          </p>
          <a href="#contact" className="btn-gold">
            Get a Tailored Quote
          </a>
        </div>
      </div>
    </section>
  );
}
